// Reading and writing Live's device parameters by meaning, not by position.
//
// The wire carries a parameter's name, value and RANGE — what Live's API
// reports, which for some devices is the display unit (a reverb's Decay
// Time in ms, an EQ band's gain in dB) and for others a normalized 0..1
// position whose display is a log sweep (EQ Eight's frequencies). Every
// reader here tells the two apart by the range and every writer inverts the
// same mapping, so a band dragged to 1 kHz in the browser lands on the raw
// value Live reads as 1 kHz, whichever way the Set reported it.
//
// Pure: device-graph.ts (what the browser hears) and the device editors
// (what it draws) share one interpretation.

import type { DeviceParam, DeviceState } from "@shared/sync/state.js";

export interface FoundParam {
  index: number;
  p: DeviceParam;
}

/** A parameter by any of several spellings, case-insensitively, with its index. */
export function findParam(d: DeviceState, ...names: string[]): FoundParam | undefined {
  const wanted = names.map((n) => n.toLowerCase());
  for (const name of wanted) {
    const index = d.params.findIndex((p) => p.name.toLowerCase() === name);
    if (index >= 0) return { index, p: d.params[index]! };
  }
  return undefined;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** A parameter's position in its range, 0..1. */
export function normOf(p: DeviceParam | undefined, fallback: number): number {
  if (!p || !(p.max > p.min)) return fallback;
  return clamp((p.value - p.min) / (p.max - p.min), 0, 1);
}

/** The raw value at position `t` (0..1) of the parameter's range. */
export function fromNorm(p: DeviceParam, t: number): number {
  return p.min + clamp(t, 0, 1) * (p.max - p.min);
}

export function logMap(t: number, lo: number, hi: number): number {
  return lo * Math.pow(hi / lo, clamp(t, 0, 1));
}

export function logUnmap(v: number, lo: number, hi: number): number {
  return clamp(Math.log(clamp(v, lo, hi) / lo) / Math.log(hi / lo), 0, 1);
}

/** A parameter reported as a 0..1 position rather than in its display unit. */
export function isNormalized(p: DeviceParam): boolean {
  return p.min >= 0 && p.max <= 1.0001 && !p.quantized;
}

/** A frequency in Hz: a normalized parameter sweeps `lo..hi` logarithmically
 * (EQ Eight's way), anything else is taken as Hz. */
export function readHz(p: DeviceParam | undefined, lo: number, hi: number, fallback: number): number {
  if (!p) return fallback;
  if (isNormalized(p)) return logMap(normOf(p, 0.5), lo, hi);
  return clamp(p.value, Math.min(lo, 10), Math.max(hi, 22000));
}

/** The raw value that `readHz` reads as `hz`. */
export function writeHz(p: DeviceParam, hz: number, lo: number, hi: number): number {
  if (isNormalized(p)) return fromNorm(p, logUnmap(hz, lo, hi));
  return clamp(hz, p.min, p.max);
}

/** A time in ms: normalized sweeps `lo..hi` ms logarithmically; a range
 * reaching past 30 counts in ms; a small one in seconds. */
export function readMs(p: DeviceParam | undefined, lo: number, hi: number, fallback: number): number {
  if (!p || p.quantized) return fallback;
  if (isNormalized(p)) return logMap(normOf(p, 0.5), lo, hi);
  return p.max > 30 ? p.value : p.value * 1000;
}

export function writeMs(p: DeviceParam, ms: number, lo: number, hi: number): number {
  if (isNormalized(p)) return fromNorm(p, logUnmap(ms, lo, hi));
  return clamp(p.max > 30 ? ms : ms / 1000, p.min, p.max);
}

/** A gain in dB: as written when the range dips below zero (Live's gains
 * are dB), else a linear amount converted. */
export function readDb(p: DeviceParam | undefined, fallback: number): number {
  if (!p) return fallback;
  if (p.min < 0) return clamp(p.value, -96, 36);
  return p.value > 0 ? clamp(20 * Math.log10(p.value), -96, 36) : -96;
}

export function writeDb(p: DeviceParam, db: number): number {
  if (p.min < 0) return clamp(db, p.min, p.max);
  return clamp(Math.pow(10, db / 20), p.min, p.max);
}

/** An amount 0..1 — a Dry/Wet whether it runs 0..1 or 0..100. */
export function readAmount(p: DeviceParam | undefined, fallback: number): number {
  return normOf(p, fallback);
}

/** On/off: a switch reads above the middle of its range. */
export function readOn(p: DeviceParam | undefined, fallback: boolean): boolean {
  if (!p) return fallback;
  return p.value > (p.min + p.max) / 2;
}

/** A quantized parameter's value name, when the Set reported names. */
export function itemOf(p: DeviceParam | undefined): string | undefined {
  if (!p?.items || p.items.length === 0) return undefined;
  return p.items[clamp(Math.round(p.value - p.min), 0, p.items.length - 1)];
}

/** The index of a quantized parameter's step from its minimum. */
export function stepOf(p: DeviceParam | undefined, fallback: number): number {
  if (!p) return fallback;
  return Math.round(p.value - p.min);
}

/** Whether a device is switched on (no "Device On" ⇒ on). */
export function deviceOn(d: DeviceState): boolean {
  return readOn(findParam(d, "Device On")?.p, true);
}

// ---- tempo-synced divisions -----------------------------------------------

/** A beat division in beats (quarter notes) from a value name: "1/4" → 1,
 * "1/8T" → 1/3, "1/16." → 0.375, "3" (sixteenths, Delay's own way) → 0.75. */
export function divisionBeats(label: string | undefined, sixteenthsIfBare = true): number | undefined {
  if (!label) return undefined;
  const s = label.trim().toLowerCase();
  const frac = /^(\d+)\s*\/\s*(\d+)\s*([td.]?)/.exec(s);
  if (frac) {
    const whole = (Number(frac[1]) / Number(frac[2])) * 4;
    const mod = frac[3] === "t" ? 2 / 3 : frac[3] === "." || frac[3] === "d" ? 1.5 : 1;
    return whole * mod;
  }
  const bars = /^(\d+)\s*bars?$/.exec(s);
  if (bars) return Number(bars[1]) * 4;
  const n = /^(\d+(?:\.\d+)?)$/.exec(s);
  if (n) return sixteenthsIfBare ? Number(n[1]) / 4 : Number(n[1]);
  return undefined;
}

/** Delay's sixteenth steps when no names were reported. */
export const DELAY_SIXTEENTHS = [1, 2, 3, 4, 5, 6, 8, 16];

// ---- EQ Eight ---------------------------------------------------------------

export type EqBandType = "lowcut48" | "lowcut12" | "lowshelf" | "bell" | "notch" | "highshelf" | "highcut12" | "highcut48";

/** Live's order of EQ Eight's filter types. */
export const EQ_BAND_TYPES: readonly EqBandType[] = ["lowcut48", "lowcut12", "lowshelf", "bell", "notch", "highshelf", "highcut12", "highcut48"];

export const EQ_BAND_LABELS: Record<EqBandType, string> = {
  lowcut48: "Low Cut 48",
  lowcut12: "Low Cut 12",
  lowshelf: "Low Shelf",
  bell: "Bell",
  notch: "Notch",
  highshelf: "High Shelf",
  highcut12: "High Cut 12",
  highcut48: "High Cut 48",
};

export const EQ_FREQ_MIN = 10;
export const EQ_FREQ_MAX = 22000;
export const EQ_Q_MIN = 0.1;
export const EQ_Q_MAX = 18;
const EQ_DEFAULT_FREQS = [30, 90, 250, 500, 1000, 2500, 6000, 15000];
const EQ_DEFAULT_TYPES: EqBandType[] = ["lowcut48", "lowshelf", "bell", "bell", "bell", "bell", "highshelf", "highcut48"];

/** A filter type from its value name ("Low Cut 48", "LowCut48", "Bell"…). */
export function eqTypeFromLabel(label: string): EqBandType | undefined {
  const s = label.toLowerCase().replace(/[^a-z0-9]/g, "");
  const steep = s.includes("48");
  if (s.includes("lowcut") || s.includes("highpass")) return steep ? "lowcut48" : "lowcut12";
  if (s.includes("highcut") || s.includes("lowpass")) return steep ? "highcut48" : "highcut12";
  if (s.includes("lowshelf")) return "lowshelf";
  if (s.includes("highshelf")) return "highshelf";
  if (s.includes("notch")) return "notch";
  if (s.includes("bell") || s.includes("peak")) return "bell";
  return undefined;
}

export interface EqBand {
  /** 1..8, as Live numbers them. */
  n: number;
  on: boolean;
  type: EqBandType;
  freq: number;
  /** dB; meaningless for cuts and the notch. */
  gain: number;
  q: number;
  /** Where each of the band's parameters sits in the device, when present. */
  idx: { on?: number; type?: number; freq?: number; gain?: number; q?: number };
}

/** Whether a band type has a gain (shelves and bells do; cuts and notches don't). */
export function eqHasGain(type: EqBandType): boolean {
  return type === "lowshelf" || type === "bell" || type === "highshelf";
}

function eqParam(d: DeviceState, n: number, what: string): FoundParam | undefined {
  return findParam(d, `${n} ${what} A`, `${n} ${what}`);
}

/** EQ Eight's bands as the Set holds them — the "A" curve (Stereo mode's,
 * and Left/Mid in the split modes). A band with none of its parameters
 * reported is left out. */
export function eqEightBands(d: DeviceState): EqBand[] {
  const out: EqBand[] = [];
  for (let n = 1; n <= 8; n++) {
    const on = eqParam(d, n, "Filter On");
    const type = eqParam(d, n, "Filter Type");
    const freq = eqParam(d, n, "Frequency");
    const gain = eqParam(d, n, "Gain");
    const q = eqParam(d, n, "Resonance");
    if (!on && !type && !freq && !gain && !q) continue;
    const label = itemOf(type?.p);
    const t = (label ? eqTypeFromLabel(label) : undefined) ?? (type ? EQ_BAND_TYPES[clamp(stepOf(type.p, 3), 0, 7)] : undefined) ?? EQ_DEFAULT_TYPES[n - 1]!;
    out.push({
      n,
      on: readOn(on?.p, true),
      type: t,
      freq: readHz(freq?.p, EQ_FREQ_MIN, EQ_FREQ_MAX, EQ_DEFAULT_FREQS[n - 1]!),
      gain: clamp(readDb(gain?.p, 0), -15, 15),
      q: q ? (isNormalized(q.p) ? logMap(normOf(q.p, 0.3), EQ_Q_MIN, EQ_Q_MAX) : clamp(q.p.value, EQ_Q_MIN, EQ_Q_MAX)) : 0.71,
      idx: { on: on?.index, type: type?.index, freq: freq?.index, gain: gain?.index, q: q?.index },
    });
  }
  return out;
}

/** The raw value for a band's Q. */
export function writeQ(p: DeviceParam, q: number): number {
  if (isNormalized(p)) return fromNorm(p, logUnmap(q, EQ_Q_MIN, EQ_Q_MAX));
  return clamp(q, p.min, p.max);
}

/** The raw value for a band type (by name when the Set reported names). */
export function writeEqType(p: DeviceParam, type: EqBandType): number {
  if (p.items && p.items.length > 0) {
    const i = p.items.findIndex((s) => eqTypeFromLabel(s) === type);
    if (i >= 0) return p.min + i;
  }
  return clamp(p.min + EQ_BAND_TYPES.indexOf(type), p.min, p.max);
}

// ---- Delay ------------------------------------------------------------------

export interface DelaySide {
  sync: boolean;
  /** Seconds, at the given tempo when synced. */
  timeSec: number;
  idx: { sync?: number; time?: number; division?: number; offset?: number };
}

export interface DelayModel {
  left: DelaySide;
  right: DelaySide;
  link: boolean;
  pingPong: boolean;
  feedback: number;
  freeze: boolean;
  filterOn: boolean;
  filterFreq: number;
  /** Bandwidth, Live's "Filter Width" (0.5..9; wider ⇒ more of the band passes). */
  filterWidth: number;
  mix: number;
  idx: { link?: number; pingPong?: number; feedback?: number; freeze?: number; filterOn?: number; filterFreq?: number; filterWidth?: number; mix?: number };
}

function delaySide(d: DeviceState, side: "L" | "R", bpm: number, fallbackMs: number): DelaySide {
  const sync = findParam(d, `${side} Sync`);
  const time = findParam(d, `${side} Time`, side === "L" ? "Delay Time" : "R Delay Time", side === "L" ? "Time" : `${side} Time`, `${side} Delay Time`);
  const division = findParam(d, `${side} 16th`, `${side} Division`, `${side} Sync Enum`, `${side} Synced`);
  const offset = findParam(d, `${side} Offset`);
  const synced = readOn(sync?.p, false) && division !== undefined;
  let timeSec: number;
  if (synced && division) {
    const label = itemOf(division.p);
    const beats = divisionBeats(label) ?? (DELAY_SIXTEENTHS[clamp(stepOf(division.p, 3), 0, DELAY_SIXTEENTHS.length - 1)]! / 4);
    const off = offset ? clamp(offset.p.value > 1 || offset.p.value < -1 ? offset.p.value / 100 : offset.p.value, -0.5, 0.5) : 0;
    timeSec = beats * (60 / Math.max(20, bpm)) * (1 + off);
  } else {
    timeSec = readMs(time?.p, 1, 5000, fallbackMs) / 1000;
  }
  return { sync: synced, timeSec: clamp(timeSec, 0.001, 5), idx: { sync: sync?.index, time: time?.index, division: division?.index, offset: offset?.index } };
}

/** Delay (and Echo / Filter Delay / Grain Delay, read with the same names
 * where they share them). `bpm` places the synced divisions. */
export function delayModel(d: DeviceState, bpm: number): DelayModel {
  const link = findParam(d, "Link");
  const ping = findParam(d, "Ping Pong", "PingPong");
  const fb = findParam(d, "Feedback", "L Feedback");
  const freeze = findParam(d, "Freeze");
  const fOn = findParam(d, "Filter On");
  const fFreq = findParam(d, "Filter Freq", "Filter Frequency");
  const fWidth = findParam(d, "Filter Width");
  const mix = findParam(d, "Dry/Wet", "Dry Wet", "Mix");
  const left = delaySide(d, "L", bpm, 375);
  const linked = readOn(link?.p, false);
  const rightRaw = delaySide(d, "R", bpm, left.timeSec * 1000);
  const hasRight = rightRaw.idx.time !== undefined || rightRaw.idx.division !== undefined;
  return {
    left,
    right: linked || !hasRight ? { ...rightRaw, sync: left.sync, timeSec: left.timeSec } : rightRaw,
    link: linked,
    pingPong: readOn(ping?.p, false),
    feedback: clamp(normOf(fb?.p, 0.3), 0, 0.95),
    freeze: readOn(freeze?.p, false),
    filterOn: readOn(fOn?.p, false),
    filterFreq: readHz(fFreq?.p, 50, 18000, 1000),
    filterWidth: fWidth ? (isNormalized(fWidth.p) ? 0.5 + normOf(fWidth.p, 0.5) * 8.5 : clamp(fWidth.p.value, 0.5, 9)) : 8,
    mix: readAmount(mix?.p, 0.5),
    idx: { link: link?.index, pingPong: ping?.index, feedback: fb?.index, freeze: freeze?.index, filterOn: fOn?.index, filterFreq: fFreq?.index, filterWidth: fWidth?.index, mix: mix?.index },
  };
}

/** The pass band of Delay's filter, from its centre and width. */
export function delayFilterBand(freq: number, width: number): { low: number; high: number } {
  const octaves = clamp(width, 0.5, 9);
  const k = Math.pow(2, octaves / 2);
  return { low: clamp(freq / k, 20, 20000), high: clamp(freq * k, 40, 22000) };
}

// ---- Reverb -----------------------------------------------------------------

export interface ReverbModel {
  predelayMs: number;
  decayMs: number;
  /** 0..1 position of Room Size on its log range. */
  size: number;
  roomSize: number;
  /** 0..1 — Stereo Image over its 120°. */
  width: number;
  mix: number;
  lowCut: number | null;
  highCut: number | null;
  inFreq: number;
  inWidth: number;
  /** High-frequency damping in the tail, 0 (none) .. 1 (dark). */
  damping: number;
  dampFreq: number;
  freeze: boolean;
  reflectDb: number;
  diffuseDb: number;
  idx: {
    predelay?: number;
    decay?: number;
    size?: number;
    width?: number;
    mix?: number;
    lowCutOn?: number;
    highCutOn?: number;
    inFreq?: number;
    inWidth?: number;
    hiShelfOn?: number;
    hiShelfFreq?: number;
    hiShelfGain?: number;
    freeze?: number;
    reflect?: number;
    diffuse?: number;
  };
}

export const REVERB_SIZE_MIN = 0.22;
export const REVERB_SIZE_MAX = 500;

/** Reverb (Hybrid Reverb read with what it shares). */
export function reverbModel(d: DeviceState): ReverbModel {
  const predelay = findParam(d, "PreDelay", "Predelay", "Pre Delay");
  const decay = findParam(d, "Decay Time", "Decay");
  const size = findParam(d, "Room Size", "Size");
  const width = findParam(d, "Stereo Image", "Stereo", "Width");
  const mix = findParam(d, "Dry/Wet", "Dry Wet", "Mix");
  const lowCutOn = findParam(d, "In LowCut On");
  const highCutOn = findParam(d, "In HighCut On");
  const inFreq = findParam(d, "In Filter Freq");
  const inWidth = findParam(d, "In Filter Width");
  const hsOn = findParam(d, "HiShelf On");
  const hsFreq = findParam(d, "HiShelf Freq");
  const hsGain = findParam(d, "HiShelf Gain");
  const freeze = findParam(d, "Freeze On", "Freeze");
  const reflect = findParam(d, "Reflect Level");
  const diffuse = findParam(d, "Diffuse Level");
  const roomSize = size ? (isNormalized(size.p) ? logMap(normOf(size.p, 0.5), REVERB_SIZE_MIN, REVERB_SIZE_MAX) : clamp(size.p.value, REVERB_SIZE_MIN, REVERB_SIZE_MAX)) : 100;
  const freq = readHz(inFreq?.p, 50, 18000, 830);
  const octaves = inWidth ? (isNormalized(inWidth.p) ? 0.5 + normOf(inWidth.p, 0.5) * 8.5 : clamp(inWidth.p.value, 0.5, 9)) : 7.5;
  const band = delayFilterBand(freq, octaves);
  const hsG = hsGain ? (hsGain.p.min < 0 ? Math.pow(10, hsGain.p.value / 20) : clamp(hsGain.p.value, 0, 1)) : 0.7;
  return {
    predelayMs: clamp(readMs(predelay?.p, 0.5, 250, 2.5), 0, 250),
    decayMs: clamp(readMs(decay?.p, 200, 60000, 1200), 100, 60000),
    size: logUnmap(roomSize, REVERB_SIZE_MIN, REVERB_SIZE_MAX),
    roomSize,
    width: width ? (isNormalized(width.p) ? normOf(width.p, 1) : clamp(width.p.value / 120, 0, 1)) : 1,
    mix: readAmount(mix?.p, 0.3),
    lowCut: readOn(lowCutOn?.p, false) ? band.low : null,
    highCut: readOn(highCutOn?.p, true) ? band.high : null,
    inFreq: freq,
    inWidth: octaves,
    damping: readOn(hsOn?.p, false) ? clamp(1 - hsG, 0, 1) : 0,
    dampFreq: readHz(hsFreq?.p, 20, 16000, 4500),
    freeze: readOn(freeze?.p, false),
    reflectDb: reflect ? clamp(readDb(reflect.p, 0), -30, 6) : 0,
    diffuseDb: diffuse ? clamp(readDb(diffuse.p, 0), -30, 6) : 0,
    idx: {
      predelay: predelay?.index,
      decay: decay?.index,
      size: size?.index,
      width: width?.index,
      mix: mix?.index,
      lowCutOn: lowCutOn?.index,
      highCutOn: highCutOn?.index,
      inFreq: inFreq?.index,
      inWidth: inWidth?.index,
      hiShelfOn: hsOn?.index,
      hiShelfFreq: hsFreq?.index,
      hiShelfGain: hsGain?.index,
      freeze: freeze?.index,
      reflect: reflect?.index,
      diffuse: diffuse?.index,
    },
  };
}

export function writeRoomSize(p: DeviceParam, size: number): number {
  if (isNormalized(p)) return fromNorm(p, logUnmap(size, REVERB_SIZE_MIN, REVERB_SIZE_MAX));
  return clamp(size, p.min, p.max);
}
